import { ChangeEvent, DragEvent, RefObject, useState } from "react";
import { VALIDATION_ERROR_AUTO_HIDE_DELAY_MS } from "../../../config";
import { useDelayedAction } from "./useDelayedAction";
import {
  hasAcceptedFileMimeType,
  useValidation,
  ValidationErrorType,
} from "./useValidation";

type UseDropzoneArgs = {
  onFilesSelected: (files: File[]) => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
};

export const useDropzone = ({
  onFilesSelected,
  fileInputRef,
}: UseDropzoneArgs) => {
  const [isDragging, setIsDragging] = useState(false);

  const {
    validateFile,
    validationErrorType,
    setValidationErrorType,
    clearValidationError,
  } = useValidation();

  const {
    run: runValidationErrorAutoHide,
    cancel: cancelValidationErrorAutoHide,
  } = useDelayedAction({
    delay: VALIDATION_ERROR_AUTO_HIDE_DELAY_MS,
    action: clearValidationError,
  });

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    for (const file of files) {
      const nextValidationMessage = validateFile(file);
      if (!nextValidationMessage.valid) {
        setValidationErrorType(nextValidationMessage.type);
        return runValidationErrorAutoHide();
      }
    }

    clearValidationError();
    onFilesSelected(files);
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    handleFiles(files);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(true);
    clearValidationError();
    cancelValidationErrorAutoHide();

    for (const item of event.dataTransfer.items) {
      if (item.kind === "file" && !hasAcceptedFileMimeType(item)) {
        setValidationErrorType(ValidationErrorType.UnsupportedFileType);
        break;
      }
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      clearValidationError();
      setIsDragging(false);
    }
  };

  return {
    isDragging,
    validationErrorType,
    openFilePicker,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
  };
};
